import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Loading } from "../../components/Loading";
import { MainButton } from "../../components/MainButton";
import { BookingHeader } from "../../components/BookingHeader";
import { ConfigType } from "../../contexts/AppConfigContext";
import { useAppConfig } from "../../hooks/useAppConfig";
import { useOrder } from "../../hooks/useOrder";
import { usePluginspace } from "../../hooks/usePluginspace";
import "./index.css";
import Swal from "sweetalert2";
import api from "../../services/api";
import InputMask from "react-input-mask";
import { Checkbox } from "../../components/Checkbox";
import { datetimeToDateBR, telephoneNoMask } from "../../functions/mask";
import { ModalBooking } from "../../components/ModalBooking";

// Importar fonte Poppins
if (typeof document !== 'undefined') {
  const link = document.createElement('link');
  link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@300;400;500;600;700;800&display=swap';
  link.rel = 'stylesheet';
  if (!document.head.querySelector('link[href*="Poppins"]')) {
    document.head.appendChild(link);
  }
}

type ImageType = {
  id: string;
  url: string;
};

type HourType = {
  id: string;
  hour: string;
};

type DayType = {
  able: boolean;
  day:
    | "sunday"
    | "monday"
    | "tuesday"
    | "wednesday"
    | "thursday"
    | "friday"
    | "saturday";
  hours: HourType[];
};

type EnvironmentType = {
  id: string;
  name: string;
  seatsAvailable: number;
  tables: number;
};

type BookingConfigType = {
  id: string;
  able: boolean;
  dayConfirm: boolean;
  expiryTime: number;
  maxPeople: number;
  paymentConfirm: boolean;
  valuePaymentConfirm: number;
  days: DayType[];
  images: ImageType[];
  environment: EnvironmentType[];
  allowCustomerConfirmation?: number;
};

type BookingType = {
  id: string;
  name: string;
  phone: string;
  people: number;
  email: string;
  date: string;
  hour: HourType;
  bookingEnvironmentId: string;
  customerArrived?: number;
  finalized?: string;
};

export function BookingPhone() {
  const { pluginspace_id, phone, code } = useParams();
  const { registerAccess, getToken } = usePluginspace();
  const navigate = useNavigate();
  const searchType = code ? 'code' : 'phone';
  const searchValue = code || phone;

  const { getAppConfig } = useAppConfig();
  const [config, setConfig] = useState<ConfigType | void>();
  const [loading, setLoading] = useState(true);
  const [bookingConfig, setBookingConfig] = useState<BookingConfigType | null>(
    null
  );
  const [bookings, setBookings] = useState<BookingType[]>([]);
  const [bookingSelected, setBookingSelected] = useState<BookingType | null>(
    null
  );
  const [openCancel, setOpenCancel] = useState(false);
  const [confirmingBooking, setConfirmingBooking] = useState<string | null>(null);

  useEffect(() => {
    registerAccess("booking/phone");
    init();
  }, []);

  async function init() {
    setLoading(true);
    await getConfig();
    await getBookingConfig();
    setLoading(false);
  }

  async function getBooking(bookingConfigId: number) {
    // Determinar o endpoint baseado no tipo de busca
    const endpoint = searchType === 'code'
      ? `/api/queueReservation/booking/byId/${searchValue}`
      : `/api/queueReservation/booking/${bookingConfigId}/${searchValue}`;
    
    const searchDescription = searchType === 'code' ? 'código de reserva' : 'número de telefone';

    await api
      .get(endpoint, {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then((response) => {
        const bookingResponse = response.data;

        if (
          bookingResponse &&
          bookingResponse.data &&
          (Array.isArray(bookingResponse.data) ? bookingResponse.data.length > 0 : bookingResponse.data)
        ) {
          // Se for busca por código, a resposta é um objeto único, não um array
          const bookingsData = Array.isArray(bookingResponse.data) 
            ? bookingResponse.data 
            : [bookingResponse.data];
          setBookings(bookingsData);
        } else {
          Swal.fire({
            icon: "warning",
            title: "Atenção!",
            text: `Não foi possível encontrar reservas com esse ${searchDescription}.`,
          }).then(() => navigate(`/${pluginspace_id}/booking`));
        }
      })
      .catch((err) => {
        let title = "Erro!";
        let text =
          `Não foi possível encontrar reservas com esse ${searchDescription}.`;
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(() => navigate(`/${pluginspace_id}/booking`));
      });
  }

  async function getBookingConfig() {
    await api
      .get("/api/queueReservation/bookingConfig", {
        headers: {
          Authorization: `Bearer ${await getToken()}`,
        },
      })
      .then(async (response) => {
        const bookingResponse = response.data;
        if (
          bookingResponse &&
          bookingResponse.data &&
          bookingResponse.data[0]
        ) {
          const bookingConfigItem = bookingResponse.data[0];
          if (bookingConfigItem && bookingConfigItem.able) {
            setBookingConfig(bookingConfigItem);
            await getBooking(bookingConfigItem.id);
          } else {
            Swal.fire({
              icon: "error",
              title: "Reserva não está habilitada!",
              text: "Loja não está com o esquema de reservas habilitada.",
            }).then(() => navigate(`/${pluginspace_id}`));
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Reserva não configurada!",
            text: "Loja não está com o esquema de reservas configurada.",
          }).then(() => navigate(`/${pluginspace_id}`));
        }
      })
      .catch((err) => {
        console.log(err);
        Swal.fire({
          icon: "error",
          title: "Reserva não configurada!",
          text: "Loja não está com o esquema de reservas configurada.",
        }).then(() => navigate(`/${pluginspace_id}`));
      });
  }

  async function cancelBooking(id: string) {
    // Encontrar a reserva correspondente
    const booking = bookings.find(b => b.id === id);
    if (!booking) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Reserva não encontrada.",
      });
      return;
    }

    setBookingSelected(null);
    setOpenCancel(false);
    setLoading(true);
    await api
      .put(
        `/api/queueReservation/booking/${id}`,
        {
          finalized: "canceled",
          bookingConfigId: bookingConfig ? parseInt(bookingConfig.id) : 0,
          bookingHourId: parseInt(booking.hour.id),
          bookingEnvironmentId: parseInt(booking.bookingEnvironmentId),
          date: booking.date,
          people: parseInt(booking.people.toString()),
          name: booking.name,
          email: booking.email,
          phone: telephoneNoMask(booking.phone),
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((response) => {
        setLoading(false);
        const bookingResponse = response.data;
        if (bookingResponse.success) {
          Swal.fire({
            icon: "success",
            title: "Sucesso!",
            text: "Reserva cancelada com suceso.",
          }).then(async () => await init());
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao cancelar reserva!",
            text: "Não foi possível cancelar reserva.",
          }).then(async () => await init());
        }
      })
      .catch((err) => {
        setLoading(false);
        let title = "Erro ao cancelar reserva!";
        let text = "Não foi possível cancelar reserva.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(async () => await init());
      });
  }

  async function confirmArrival(id: string) {
    // Encontrar a reserva correspondente
    const booking = bookings.find(b => b.id === id);
    if (!booking) {
      Swal.fire({
        icon: "error",
        title: "Erro!",
        text: "Reserva não encontrada.",
      });
      return;
    }

    setConfirmingBooking(id);
    await api
      .put(
        `/api/queueReservation/booking/${id}`,
        {
          customerArrived: 1,
          bookingConfigId: bookingConfig ? parseInt(bookingConfig.id) : 0,
          bookingHourId: parseInt(booking.hour.id),
          bookingEnvironmentId: parseInt(booking.bookingEnvironmentId),
          date: booking.date,
          people: parseInt(booking.people.toString()),
          name: booking.name,
          email: booking.email,
          phone: telephoneNoMask(booking.phone),
        },
        {
          headers: {
            Authorization: `Bearer ${await getToken()}`,
          },
        }
      )
      .then((response) => {
        setConfirmingBooking(null);
        const bookingResponse = response.data;
        if (bookingResponse.success) {
          Swal.fire({
            icon: "success",
            title: "Confirmado!",
            text: "Sua chegada foi confirmada com sucesso. Por favor, aguarde ser chamado.",
          }).then(async () => await init());
        } else {
          Swal.fire({
            icon: "error",
            title: "Erro ao confirmar!",
            text: "Não foi possível confirmar sua chegada.",
          }).then(async () => await init());
        }
      })
      .catch((err) => {
        setConfirmingBooking(null);
        let title = "Erro ao confirmar!";
        let text = "Não foi possível confirmar sua chegada.";
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.error
        ) {
          title = err.response.data.error;
        }
        if (
          err &&
          err.response &&
          err.response.data &&
          err.response.data.message
        ) {
          text = err.response.data.message;
        }
        Swal.fire({
          icon: "error",
          title,
          text,
        }).then(async () => await init());
      });
  }

  async function getConfig() {
    setConfig(await getAppConfig());
  }

  function isBookingValid(booking: BookingType): boolean {
    // Verifica se a reserva não foi cancelada ou finalizada
    if (booking.finalized === "canceled" || booking.finalized === "finished") {
      return false;
    }

    // Verifica se a reserva já foi confirmada
    if (booking.customerArrived === 1) {
      return false;
    }

    // Verifica se a data da reserva é hoje ou futura
    const bookingDate = new Date(booking.date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    bookingDate.setHours(0, 0, 0, 0);

    return bookingDate >= today;
  }

  return (
    <>
      <BookingHeader />
      <div className="booking-find-phone">
        {loading ? (
          <div style={{ width: "100%", height: "calc(100vh)" }}>
            <Loading
              type="spinningBubbles"
              color="#FFF"
              width={64}
              height={64}
            />
          </div>
        ) : (
          <div className="content-booking-find-phone">
            {bookingConfig && (
              <div className="body-booking-find-phone">
                <h2 style={{
                  fontFamily: "'Poppins', sans-serif",
                  fontWeight: "800",
                  fontSize: "26px",
                  marginBottom: "28px",
                  color: "#2d3436",
                  textAlign: "center",
                  letterSpacing: "-0.5px",
                  textShadow: "0 2px 8px rgba(45, 52, 54, 0.1)"
                }}>
                  {searchType === 'code' 
                    ? `🎫 Reserva encontrada com o código #${searchValue}` 
                    : '📋 Encontramos essas reservas para o celular informado'
                  }
                </h2>
                {bookings.map((booking, index) => (
                  <div 
                    key={booking.id}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.transform = "translateY(-4px)";
                      e.currentTarget.style.boxShadow = "0 16px 40px rgba(33, 150, 243, 0.25), 0 8px 16px rgba(33, 150, 243, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.6)";
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.transform = "translateY(0)";
                      e.currentTarget.style.boxShadow = "0 10px 32px rgba(33, 150, 243, 0.18), 0 6px 16px rgba(33, 150, 243, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)";
                    }}
                    style={{
                      background: "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(30, 136, 229, 0.05) 50%, rgba(25, 118, 210, 0.06) 100%)",
                      backdropFilter: "blur(20px)",
                      WebkitBackdropFilter: "blur(20px)",
                      borderRadius: "20px",
                      padding: "clamp(16px, 4vw, 28px)",
                      marginBottom: index < bookings.length - 1 ? "24px" : "20px",
                      maxWidth: "min(1100px, calc(100vw - 40px))",
                      marginLeft: "auto",
                      marginRight: "auto",
                      boxShadow: "0 10px 32px rgba(33, 150, 243, 0.18), 0 6px 16px rgba(33, 150, 243, 0.12), inset 0 1px 0 rgba(255, 255, 255, 0.5)",
                      border: "1px solid rgba(33, 150, 243, 0.2)",
                      position: "relative" as const,
                      overflow: "hidden",
                      transition: "all 0.4s cubic-bezier(0.4, 0, 0.2, 1)"
                    }}
                  >
                    {/* Overlay de brilho */}
                    <div style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      background: "linear-gradient(135deg, rgba(255, 255, 255, 0.15) 0%, rgba(255, 255, 255, 0) 100%)",
                      pointerEvents: "none"
                    }}></div>
                    
                    <div style={{ position: "relative", zIndex: 1 }}>
                      {/* Número da Reserva - Card destacado */}
                      <div style={{ 
                        background: "linear-gradient(135deg, rgba(76, 175, 80, 0.15) 0%, rgba(67, 160, 71, 0.1) 100%)",
                        backdropFilter: "blur(10px)",
                        padding: "12px 16px", 
                        borderRadius: "12px",
                        marginBottom: "16px",
                        border: "1px solid rgba(76, 175, 80, 0.25)",
                        boxShadow: "0 4px 12px rgba(76, 175, 80, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                      }}>
                        <div style={{ 
                          fontFamily: "'Poppins', sans-serif",
                          color: "#2e7d32", 
                          fontSize: "11px",
                          fontWeight: "600",
                          textTransform: "uppercase",
                          letterSpacing: "1px",
                          marginBottom: "4px"
                        }}>🎫 Reserva</div>
                        <div style={{ 
                          fontFamily: "'Poppins', sans-serif",
                          color: "#1b5e20", 
                          fontSize: "clamp(18px, 4vw, 22px)", 
                          fontWeight: "800",
                          letterSpacing: "-0.5px"
                        }}>
                          #{booking.id}
                        </div>
                      </div>
                      
                      {/* Grid de informações - Responsivo */}
                      <div style={{ 
                        display: "grid", 
                        gap: "clamp(8px, 2vw, 12px)",
                        gridTemplateColumns: "repeat(auto-fit, minmax(min(280px, 100%), 1fr))"
                      }}>
                        {/* Data e Horário */}
                        <div style={{
                          background: "linear-gradient(135deg, rgba(33, 150, 243, 0.1) 0%, rgba(30, 136, 229, 0.05) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                          borderRadius: "12px",
                          border: "1px solid rgba(33, 150, 243, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          gap: "clamp(8px, 2vw, 12px)"
                        }}>
                          <span style={{
                            fontSize: "20px",
                            lineHeight: 1
                          }}>📅</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#0d47a1",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px"
                            }}>Data e Horário</div>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(13px, 3vw, 15px)",
                              fontWeight: "700",
                              color: "#1565c0"
                            }}>
                              {datetimeToDateBR(booking.date)} {booking.hour.hour}
                            </div>
                          </div>
                        </div>
                        
                        {/* Pessoas */}
                        <div style={{
                          background: "linear-gradient(135deg, rgba(156, 39, 176, 0.1) 0%, rgba(142, 36, 170, 0.05) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                          borderRadius: "12px",
                          border: "1px solid rgba(156, 39, 176, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          gap: "clamp(8px, 2vw, 12px)"
                        }}>
                          <span style={{
                            fontSize: "20px",
                            lineHeight: 1
                          }}>👥</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#6a1b9a",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px"
                            }}>Pessoas</div>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(13px, 3vw, 15px)",
                              fontWeight: "700",
                              color: "#4a148c"
                            }}>
                              {booking.people} {booking.people > 1 ? "pessoas" : "pessoa"}
                            </div>
                          </div>
                        </div>
                        
                        {/* Nome */}
                        <div style={{
                          background: "linear-gradient(135deg, rgba(255, 152, 0, 0.1) 0%, rgba(251, 140, 0, 0.05) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                          borderRadius: "12px",
                          border: "1px solid rgba(255, 152, 0, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          gap: "clamp(8px, 2vw, 12px)"
                        }}>
                          <span style={{
                            fontSize: "20px",
                            lineHeight: 1
                          }}>👤</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "11px",
                              fontWeight: "600",
                              color: "#e65100",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px"
                            }}>Nome</div>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(13px, 3vw, 15px)",
                              fontWeight: "700",
                              color: "#bf360c"
                            }}>
                              {booking.name}
                            </div>
                          </div>
                        </div>
                        
                        {/* Ambiente */}
                        {bookingConfig.environment?.length > 0 && (
                          bookingConfig.environment
                            .filter(({ id }: any) => booking?.bookingEnvironmentId === id)
                            .map(({ id, name }: any) => (
                              <div key={id} style={{
                                background: "linear-gradient(135deg, rgba(76, 175, 80, 0.1) 0%, rgba(67, 160, 71, 0.05) 100%)",
                                backdropFilter: "blur(10px)",
                                padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                                borderRadius: "12px",
                                border: "1px solid rgba(76, 175, 80, 0.2)",
                                display: "flex",
                                alignItems: "center",
                                gap: "clamp(8px, 2vw, 12px)"
                              }}>
                                <span style={{
                                  fontSize: "20px",
                                  lineHeight: 1
                                }}>📍</span>
                                <div style={{ flex: 1 }}>
                                  <div style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: "clamp(10px, 2.5vw, 11px)",
                                    fontWeight: "600",
                                    color: "#2e7d32",
                                    textTransform: "uppercase",
                                    letterSpacing: "0.5px",
                                    marginBottom: "2px"
                                  }}>Ambiente</div>
                                  <div style={{
                                    fontFamily: "'Poppins', sans-serif",
                                    fontSize: "clamp(13px, 3vw, 15px)",
                                    fontWeight: "700",
                                    color: "#1b5e20"
                                  }}>
                                    {name}
                                  </div>
                                </div>
                              </div>
                            ))
                        )}
                        
                        {/* Telefone */}
                        <div style={{
                          background: "linear-gradient(135deg, rgba(0, 188, 212, 0.1) 0%, rgba(0, 172, 193, 0.05) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                          borderRadius: "12px",
                          border: "1px solid rgba(0, 188, 212, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          gap: "clamp(8px, 2vw, 12px)"
                        }}>
                          <span style={{
                            fontSize: "20px",
                            lineHeight: 1
                          }}>📞</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(10px, 2.5vw, 11px)",
                              fontWeight: "600",
                              color: "#00838f",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px"
                            }}>Telefone</div>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(13px, 3vw, 15px)",
                              fontWeight: "700",
                              color: "#006064"
                            }}>
                              {booking.phone}
                            </div>
                          </div>
                        </div>
                        
                        {/* E-mail */}
                        <div style={{
                          background: "linear-gradient(135deg, rgba(233, 30, 99, 0.1) 0%, rgba(216, 27, 96, 0.05) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)",
                          borderRadius: "12px",
                          border: "1px solid rgba(233, 30, 99, 0.2)",
                          display: "flex",
                          alignItems: "center",
                          gap: "clamp(8px, 2vw, 12px)"
                        }}>
                          <span style={{
                            fontSize: "20px",
                            lineHeight: 1
                          }}>✉️</span>
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(10px, 2.5vw, 11px)",
                              fontWeight: "600",
                              color: "#880e4f",
                              textTransform: "uppercase",
                              letterSpacing: "0.5px",
                              marginBottom: "2px"
                            }}>E-mail</div>
                            <div style={{
                              fontFamily: "'Poppins', sans-serif",
                              fontSize: "clamp(13px, 3vw, 14px)",
                              fontWeight: "600",
                              color: "#ad1457",
                              wordBreak: "break-all" as const
                            }}>
                              {booking.email}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status da Reserva */}
                      {booking.customerArrived === 1 && (
                        <div style={{ 
                          marginTop: "clamp(12px, 3vw, 16px)",
                          background: "linear-gradient(135deg, rgba(76, 175, 80, 0.2) 0%, rgba(67, 160, 71, 0.15) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)", 
                          borderRadius: "12px",
                          border: "1px solid rgba(76, 175, 80, 0.3)",
                          boxShadow: "0 4px 12px rgba(76, 175, 80, 0.2), inset 0 1px 0 rgba(255, 255, 255, 0.5)"
                        }}>
                          <div style={{
                            fontFamily: "'Poppins', sans-serif",
                            color: "#1b5e20",
                            fontWeight: "700",
                            fontSize: "clamp(13px, 3vw, 14px)",
                            display: "flex",
                            alignItems: "center",
                            gap: "clamp(6px, 1.5vw, 8px)"
                          }}>
                            <span style={{ fontSize: "18px" }}>✓</span>
                            Chegada confirmada
                          </div>
                        </div>
                      )}
                      
                      {booking.finalized === "canceled" && (
                        <div style={{ 
                          marginTop: "clamp(12px, 3vw, 16px)",
                          background: "linear-gradient(135deg, rgba(244, 67, 54, 0.15) 0%, rgba(229, 57, 53, 0.1) 100%)",
                          backdropFilter: "blur(10px)",
                          padding: "clamp(10px, 2.5vw, 12px) clamp(12px, 3vw, 16px)", 
                          borderRadius: "12px",
                          border: "1px solid rgba(244, 67, 54, 0.25)",
                          boxShadow: "0 4px 12px rgba(244, 67, 54, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.4)"
                        }}>
                          <div style={{
                            fontFamily: "'Poppins', sans-serif",
                            color: "#b71c1c",
                            fontWeight: "700",
                            fontSize: "clamp(13px, 3vw, 14px)",
                            display: "flex",
                            alignItems: "center",
                            gap: "clamp(6px, 1.5vw, 8px)"
                          }}>
                            <span style={{ fontSize: "18px" }}>✗</span>
                            Reserva cancelada
                          </div>
                        </div>
                      )}

                      <div style={{ 
                        marginTop: "clamp(16px, 4vw, 20px)",
                        display: "flex",
                        flexDirection: "column" as const,
                        gap: "clamp(10px, 2.5vw, 12px)"
                      }}>
                        {/* Botão de Confirmar Chegada - Só aparece se: */}
                        {/* 1. allowCustomerConfirmation está habilitado */}
                        {/* 2. A reserva é vigente (não cancelada, não confirmada, data válida) */}
                        {bookingConfig.allowCustomerConfirmation === 1 && 
                         isBookingValid(booking) && (
                          <div style={{ width: "100%" }}>
                            <div
                              onMouseEnter={(e) => {
                                e.currentTarget.style.transform = "translateY(-2px)";
                                e.currentTarget.style.boxShadow = "0 8px 16px rgba(40, 167, 69, 0.3)";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.transform = "translateY(0)";
                                e.currentTarget.style.boxShadow = "0 4px 8px rgba(40, 167, 69, 0.15)";
                              }}
                              style={{
                                transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                                borderRadius: "10px",
                                boxShadow: "0 4px 8px rgba(40, 167, 69, 0.15)"
                              }}
                            >
                              <MainButton
                                color="#FFF"
                                backgroundColor="#28a745"
                                border="1px solid #28a745"
                                text={confirmingBooking === booking.id ? "Confirmando..." : "Confirmar Chegada"}
                                onClick={() => {
                                  if (confirmingBooking === null) {
                                    Swal.fire({
                                      title: "Confirmar Chegada",
                                      text: "Você confirma que já chegou ao restaurante?",
                                      icon: "question",
                                      showCancelButton: true,
                                      confirmButtonText: "Sim, cheguei!",
                                      cancelButtonText: "Cancelar",
                                      confirmButtonColor: "#28a745",
                                    }).then((result) => {
                                      if (result.isConfirmed) {
                                        confirmArrival(booking.id);
                                      }
                                    });
                                  }
                                }}
                              />
                            </div>
                          </div>
                        )}
                        
                        <div style={{ 
                          display: "flex", 
                          gap: "clamp(10px, 2.5vw, 12px)",
                          width: "100%",
                          flexDirection: window.innerWidth <= 640 ? "column" as const : "row" as const
                        }}>
                          <div
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 16px rgba(33, 150, 243, 0.25)";
                              e.currentTarget.style.background = "linear-gradient(135deg, rgba(33, 150, 243, 0.08) 0%, rgba(30, 136, 229, 0.12) 100%)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(33, 150, 243, 0.15)";
                              e.currentTarget.style.background = "transparent";
                            }}
                            style={{
                              flex: 1,
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              borderRadius: "10px",
                              boxShadow: "0 2px 8px rgba(33, 150, 243, 0.15)"
                            }}
                          >
                            <MainButton
                              color={
                                config?.webappcolor.webappbutton
                                  ? config.webappcolor.webappbutton
                                  : "#2196F3"
                              }
                              border={
                                config?.webappcolor.webappbutton
                                  ? `1px solid ${config.webappcolor.webappbutton}`
                                  : "1px solid #2196F3"
                              }
                              backgroundColor="transparent"
                              text="✏️ Alterar"
                              onClick={() => {
                                navigate(
                                  `/${pluginspace_id}/booking/update/${booking.id}`
                                );
                              }}
                            />
                          </div>
                          
                          <div
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = "translateY(-2px)";
                              e.currentTarget.style.boxShadow = "0 8px 16px rgba(244, 67, 54, 0.25)";
                              e.currentTarget.style.background = "linear-gradient(135deg, rgba(244, 67, 54, 0.08) 0%, rgba(229, 57, 53, 0.12) 100%)";
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = "translateY(0)";
                              e.currentTarget.style.boxShadow = "0 2px 8px rgba(244, 67, 54, 0.15)";
                              e.currentTarget.style.background = "transparent";
                            }}
                            style={{
                              flex: 1,
                              transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                              borderRadius: "10px",
                              boxShadow: "0 2px 8px rgba(244, 67, 54, 0.15)"
                            }}
                          >
                            <MainButton
                              color={
                                config?.webappcolor.webappbutton
                                  ? config.webappcolor.webappbutton
                                  : "#f44336"
                              }
                              border={
                                config?.webappcolor.webappbutton
                                  ? `1px solid ${config.webappcolor.webappbutton}`
                                  : "1px solid #f44336"
                              }
                              backgroundColor="transparent"
                              text="✗ Cancelar"
                              onClick={() => {
                                setBookingSelected(booking);
                                setOpenCancel(true);
                              }}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
      {openCancel && config && bookingSelected && (
        <ModalBooking
          action={cancelBooking}
          config={config}
          isOpen={openCancel}
          booking={bookingSelected}
          setIsOpen={setOpenCancel}
        />
      )}
    </>
  );
}
